import { map } from 'rxjs/operators';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { FromEventTarget } from 'rxjs/internal/observable/fromEvent';
import setup from './setup';
import setupQ2Elements from './setup/elements';

import setupActions from './actions';
import setupSources from './sources';

import { IDict } from './types/utility';
import { Maybe } from 'maybe-not';
import SDKMessageBus from './utility/message-bus';
import t from './setup/t';
import resizeIframe from './actions/resizeIframe';
import navigateTo from './actions/navigateTo';
import showOverpanel from './actions/showOverpanel';
import resolveMenu from './setup/resolveMenu';
import getParameterByName from './utility/getParameterByName';
import { TestingWindow } from 'q2-tecton-common/lib/utility/message-bus';
import { IConnectOptions, IPlatformCoreAPI } from './types';
import { createGlobalScripts, setupGlobalScripts } from './setup/globalScripts';

/**
 * Initializes the Tecton environment for a module.  This function should be the first called when initializing an module.
 *
 * Legacy in this case refers to the approach of adding the SDK at the platform level to enable the platform to use the elements. So named because the plan is for all platforms to eventually be made of tecton extensions.
 * Below is an example setup of a module initializing a VueJS environment.
 *
 * ```javascript
 * import { connect } from 'q2-tecton-sdk';
 * import Vue from 'vue';
 *
 * connect().then(({ sources, actions, params$ }: any) => {
 *   let stateParams = params$.getValue();
 *   new Vue({
 *     el: '#app',
 *     //setup Vue environment
 *     stateParams,
 *     tecton: { sources, actions }
 *   });
 *
 * });
 * ```
 * @param options Options for connecting.
 * @returns A Promise, that when resolved, contains the Tecton SDK API available to the module.
 */

export function connect(options?: IConnectOptions): Promise<IPlatformCoreAPI> {
    const teardowns: (() => void)[] = [];
    const legacy = (options && options.legacy) || false;

    if (options && options.testOptions) {
        const params$ = new BehaviorSubject<IDict<string>>(options.testOptions.initialParams || {});
        const teardowns: (() => void)[] = [];
        const locStub =
            options.testOptions.sources?.loc ||
            function(x: string) {
                return x;
            };
        const resizeIframeStub = () => {
            // noop
        };
        const emptyObj = {};
        const resolveMenuStub = Maybe.just(() => Promise.resolve([]));
        const navigateToStub = Maybe.just(() => emptyObj);
        const showOverpanelStub = Maybe.just(<T>() => Promise.resolve(emptyObj as T));
        const setParamsStub = (newParams: IDict<any>) => {
            params$.next({ ...params$.getValue(), ...newParams });
        };
        const printWindowStub = () => {
            // noop
        };
        const setFetchingStub = (isFetching: boolean) => {
            // noop
        };
        const paramsChangedStub = (callback: (params: IDict<any>) => void) => {
            const sub = params$.pipe(map(x => x)).subscribe(callback);
            const unsub = () => sub.unsubscribe();
            teardowns.push(unsub);
            return unsub;
        };

        const getCurrentLanguageCodeStub = () => {
            return 'en-US';
        };
        const elementPromise = options.testOptions.loadElements
            ? setupQ2Elements(
                  locStub,
                  resizeIframeStub,
                  resolveMenuStub,
                  navigateToStub,
                  showOverpanelStub,
                  teardowns,
                  Promise.resolve(Maybe.nothing<string>())
              )
            : Promise.resolve();

        return elementPromise.then(() => {
            return {
                sources: {
                    loc: locStub,
                    t: locStub,
                    getCurrentLanguageCode: getCurrentLanguageCodeStub,
                    paramsChanged: paramsChangedStub,
                    ...((options.testOptions && options.testOptions.sources) || {})
                },
                actions: {
                    setParams: setParamsStub,
                    printWindow: printWindowStub,
                    resizeIframe: resizeIframeStub,
                    setFetching: setFetchingStub,
                    ...((options.testOptions && options.testOptions.actions) || {})
                },
                outletContext: options.testOptions && options.testOptions.outletContext,
                teardown: () => {
                    teardowns.forEach(teardown => teardown());
                }
            };
        });
    }

    if (legacy) {
        /**
         * Adds SDK capabilities to the platform level
         */
        const messageBus = new SDKMessageBus();
        const fetchedPlatformCapabilities = messageBus
            .sendRequest<{ capabilities: string[] }>(
                'getPlatformCapabilities',
                Maybe.nothing<string>()
            )
            .then(response => {
                return new Set(response.value.capabilities);
            });
        fetchedPlatformCapabilities;

        return Promise.all([
            fetchedPlatformCapabilities,
            t(messageBus, 'legacy', new Set(), true, options && options.baseLanguagePath)
        ])
            .then(([platformCapabilities, tSetup]) => {
                const resolveMenuFn = Maybe.maybe(resolveMenu(messageBus, 'legacy'));
                const navigateToFn = Maybe.maybe(navigateTo(platformCapabilities, 'legacy'));
                const showOverpanelFn = Maybe.maybe(
                    showOverpanel(messageBus, 'legacy', platformCapabilities)
                );
                return setup(
                    messageBus,
                    'legacy',
                    tSetup.tFn,
                    platformCapabilities,
                    teardowns,
                    () => {
                        // noop
                    },
                    resolveMenuFn,
                    navigateToFn,
                    showOverpanelFn
                );
            })
            .then(setupTeardownFn => {
                return {
                    teardown() {
                        teardowns.forEach(fn => fn());
                        setupTeardownFn();
                        messageBus.destroy();
                    }
                };
            });
    }

    // GET MODULE ID FROM QUERY PARAMS
    const moduleId = (window as TestingWindow).isTestingTecton
        ? (options && options.moduleId) || 'tectonRoute.TESTMODULE'
        : getParameterByName('tct-id');

    const outletContext = getParameterByName('tct-ctxid');

    const params$ = new BehaviorSubject<IDict<string>>({});

    const messageBus = new SDKMessageBus();

    const maxHeight = getParameterByName('tct-maxHeight');
    if (maxHeight) {
        document.querySelector('body')?.style.setProperty('--tct-outlet-max-height', maxHeight);
    }
    const maxWidth = getParameterByName('tct-maxWidth');
    if (maxWidth) {
        document.querySelector('body')?.style.setProperty('--tct-outlet-max-width', maxWidth);
    }

    const fetchedPlatformCapabilities = messageBus
        .sendRequest<{ capabilities: string[] }>('getPlatformCapabilities', Maybe.nothing<string>())
        .then(response => {
            return new Set(response.value.capabilities);
        });

    messageBus
        .sendRequest<{
            params: IDict<string>;
        }>('getInitialParams', Maybe.just(JSON.stringify({ moduleId })))
        .then(response => {
            response.value.params && params$.next(response.value.params);
        });

    const paramSubscription = messageBus
        .onBroadcast<{ currentParams: IDict<string> }>(`streamParams::${moduleId}`)
        .pipe(
            map(response => {
                return response.value.currentParams;
            })
        )
        .subscribe(params$);

    return fetchedPlatformCapabilities
        .then(platformCapabilities =>
            Promise.all([
                t(
                    messageBus,
                    moduleId,
                    platformCapabilities,
                    false,
                    options && options.baseLanguagePath
                ),
                fetchedPlatformCapabilities
            ])
        )
        .then(([tSetup, platformCapabilities]) => {
            const resizeIframeFn = resizeIframe(moduleId, platformCapabilities, teardowns);
            const resolveMenuFn = Maybe.maybe(resolveMenu(messageBus, moduleId));
            const navigateToFn = Maybe.maybe(navigateTo(platformCapabilities, moduleId));
            const showOverpanelFn = Maybe.maybe(
                showOverpanel(messageBus, moduleId, platformCapabilities)
            );
            return setup(
                messageBus,
                moduleId,
                tSetup.tFn,
                platformCapabilities,
                teardowns,
                resizeIframeFn,
                resolveMenuFn,
                navigateToFn,
                showOverpanelFn
            ).then(setupTeardownFn => {
                return {
                    setupTeardownFn,
                    tSetup,
                    paramSubscription,
                    platformCapabilities,
                    resizeIframeFn
                };
            });
        })
        .then(
            ({
                setupTeardownFn,
                tSetup,
                paramSubscription,
                platformCapabilities,
                resizeIframeFn
            }) => {
                const actions = setupActions(
                    messageBus,
                    moduleId,
                    teardowns,
                    platformCapabilities,
                    resizeIframeFn
                );
                const sources = setupSources(
                    messageBus,
                    moduleId,
                    tSetup,
                    teardowns,
                    platformCapabilities,
                    params$
                );
                setupGlobalScripts(messageBus, platformCapabilities, sources).then(
                    scriptsResult => {
                        createGlobalScripts(scriptsResult);
                    }
                );
                const iframeTeardown = fromEvent(
                    window as FromEventTarget<any>,
                    'unload'
                ).subscribe(() => {
                    teardown();
                });
                const teardown = () => {
                    teardowns.forEach(fn => fn());
                };

                teardowns.push(() => {
                    tSetup.streamLanguageSubscription.unsubscribe();
                    paramSubscription.unsubscribe();
                    setupTeardownFn();
                    iframeTeardown.unsubscribe();
                    messageBus.destroy();
                });

                const result = {
                    sources,
                    actions,
                    outletContext,
                    teardown
                };

                return result;
            }
        );
}
